import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import companyService from '../services/companyService';
import { adminService } from '../services/adminService';
import api from '../services/api';
import { copyTextToClipboard } from '../utils/clipboard';
import { useModal } from '../context/ModalContext';

const TABS = [
  { key: 'templates', label: 'Templates' },
  { key: 'links', label: 'Links' },
  { key: 'submissions', label: 'Submissions' },
];

const styles = {
  page: { maxWidth: 1280, margin: '0 auto' },
  back: {
    border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 8,
    padding: '8px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 16,
  },
  head: { display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' },
  title: { margin: 0, color: '#111827', fontSize: 23, fontWeight: 700 },
  sub: { margin: '5px 0 0', color: '#6b7280', fontSize: 13 },
  status: (active) => ({
    display: 'inline-flex', padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
    color: active ? '#166534' : '#991b1b', background: active ? '#dcfce7' : '#fee2e2',
  }),
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, margin: '20px 0' },
  metric: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px' },
  metricValue: { color: '#111827', fontSize: 24, fontWeight: 700 },
  metricLabel: { color: '#6b7280', fontSize: 11.5, fontWeight: 650, marginTop: 4 },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' },
  tabs: { display: 'flex', gap: 22, padding: '0 18px', borderBottom: '1px solid #e5e7eb', overflowX: 'auto' },
  tab: {
    border: 'none', borderBottom: '2px solid transparent', background: 'transparent', color: '#6b7280',
    padding: '15px 2px 13px', fontSize: 13.5, fontWeight: 650, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  tabActive: { color: 'var(--company-primary-color, #273947)', borderBottomColor: 'var(--company-primary-color, #273947)' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: 14, flexWrap: 'wrap' },
  search: { width: 330, maxWidth: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 11px', fontSize: 13 },
  count: { color: '#6b7280', fontSize: 12.5 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '11px 15px', color: '#6b7280', background: '#f9fafb',
    borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', fontSize: 11,
    fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap',
  },
  td: { padding: '13px 15px', color: '#1f2937', borderBottom: '1px solid #f3f4f6', fontSize: 13, verticalAlign: 'middle' },
  subtle: { color: '#6b7280' },
  badge: (bg, fg) => ({ display: 'inline-flex', padding: '3px 8px', borderRadius: 999, background: bg, color: fg, fontSize: 11, fontWeight: 700 }),
  action: {
    border: '1px solid #d1d5db', borderRadius: 7, background: '#fff', color: '#374151',
    padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6,
  },
  empty: { padding: 42, color: '#6b7280', fontSize: 13, textAlign: 'center' },
  error: { padding: '11px 14px', marginTop: 16, borderRadius: 8, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' },
  overlay: { position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(15,23,42,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal: { width: '92%', maxWidth: 1200, height: '90%', background: '#fff', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb' },
  close: { border: 'none', background: 'transparent', color: '#64748b', fontSize: 26, cursor: 'pointer' },
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function CompanyActivityPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useModal();
  const [company, setCompany] = useState(null);
  const [forms, setForms] = useState([]);
  const [links, setLinks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('templates');
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [companyData, allForms, allLinks, submissionData, assignedIds] = await Promise.all([
        companyService.getById(companyId),
        adminService.getAllForms(),
        adminService.getAllLinks(),
        adminService.getAllSubmissions(),
        adminService.getClinicAssignedForms(companyId).catch(() => []),
      ]);
      const assigned = new Set(Array.isArray(assignedIds) ? assignedIds : []);
      const formList = (Array.isArray(allForms) ? allForms : []).filter(
        (form) => form.clinicId === companyId || assigned.has(form.id),
      );
      const linkList = (Array.isArray(allLinks) ? allLinks : []).filter((link) => link.clinicId === companyId);
      const submissionList = (Array.isArray(submissionData?.submissions) ? submissionData.submissions : []).filter((item) => {
        if (item.clinicId) return item.clinicId === companyId;
        return (item.clinicCode && item.clinicCode === companyData?.code)
          || (item.clinicName && item.clinicName === companyData?.name);
      });
      setCompany(companyData);
      setForms(formList);
      setLinks(linkList);
      setSubmissions(submissionList);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load company activity.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  const activeLinks = useMemo(() => links.filter((link) => !link.isExpired).length, [links]);
  const term = search.trim().toLowerCase();
  const visibleForms = useMemo(() => forms.filter((form) => !term || [form.name, form.description, form.status]
    .some((value) => String(value || '').toLowerCase().includes(term))), [forms, term]);
  const visibleLinks = useMemo(() => links.filter((link) => !term || [link.formName, link.patientName, link.patientEmail, link.doctorEmail, link.notes, link.token]
    .some((value) => String(value || '').toLowerCase().includes(term))), [links, term]);
  const visibleSubmissions = useMemo(() => submissions.filter((item) => !term || [item.formName, item.patientName, item.filename, item.referenceId, item.status]
    .some((value) => String(value || '').toLowerCase().includes(term))), [submissions, term]);

  const previewSubmission = async (submission) => {
    try {
      const response = await api.get(`/forms/${submission.formId}/submissions/${encodeURIComponent(submission.filename)}`, {
        params: { preview: true }, responseType: 'blob',
      });
      if (preview?.url) URL.revokeObjectURL(preview.url);
      setPreview({ name: submission.filename, url: URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })) });
    } catch (err) {
      await showAlert(err?.response?.data?.error || 'Could not preview this submission.', 'Preview failed');
    }
  };

  const copyLink = async (link) => {
    const url = link.url || `${window.location.origin}/fill/${link.token}`;
    const copied = await copyTextToClipboard(url);
    await showAlert(copied ? 'The link has been copied.' : url, copied ? 'Link copied' : 'Copy this link');
  };

  const currentCount = activeTab === 'templates' ? visibleForms.length : activeTab === 'links' ? visibleLinks.length : visibleSubmissions.length;

  return (
    <div style={styles.page}>
      <button type="button" style={styles.back} onClick={() => navigate('/admin/companies')}>← Back to companies</button>

      <div style={styles.head}>
        <div>
          <h1 style={styles.title}>{company?.name || 'Company activity'}</h1>
          <p style={styles.sub}>{company?.domain || company?.code || 'Tenant operations and support view'}</p>
        </div>
        {company && <span style={styles.status(company.isActive !== false)}>{company.isActive !== false ? 'Active' : 'Suspended'}</span>}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.metrics}>
        {[
          ['Templates', forms.length],
          ['Links', links.length],
          ['Active links', activeLinks],
          ['Submissions', submissions.length],
        ].map(([label, value]) => (
          <div key={label} style={styles.metric}>
            <div style={styles.metricValue}>{loading ? '—' : value}</div>
            <div style={styles.metricLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.tabs}>
          {TABS.map((tab) => (
            <button
              type="button"
              key={tab.key}
              style={{ ...styles.tab, ...(activeTab === tab.key ? styles.tabActive : {}) }}
              onClick={() => { setActiveTab(tab.key); setSearch(''); }}
            >
              {tab.label} ({tab.key === 'templates' ? forms.length : tab.key === 'links' ? links.length : submissions.length})
            </button>
          ))}
        </div>

        <div style={styles.toolbar}>
          <input
            aria-label={`Search ${activeTab}`}
            style={styles.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={`Search ${activeTab}…`}
          />
          <div style={styles.count}>{currentCount} result{currentCount === 1 ? '' : 's'}</div>
        </div>

        {loading ? <div style={styles.empty}>Loading company activity…</div> : (
          <>
            {activeTab === 'templates' && (
              visibleForms.length === 0 ? <div style={styles.empty}>No templates found for this company.</div> :
              <div style={styles.tableWrap}><table style={styles.table}>
                <thead><tr><th style={styles.th}>Template</th><th style={styles.th}>Type</th><th style={styles.th}>Status</th><th style={styles.th}>Last modified</th><th style={styles.th}>Action</th></tr></thead>
                <tbody>{visibleForms.map((form) => <tr key={form.id}>
                  <td style={styles.td}><div style={{ fontWeight: 650 }}>{form.name}</div><div style={{ ...styles.subtle, fontSize: 11.5 }}>{form.description || form.templateFileName || form.id}</div></td>
                  <td style={styles.td}>{form.isInternal ? 'Internal' : 'External'}</td>
                  <td style={styles.td}><span style={styles.badge('#eef2f7', '#475569')}>{form.status || 'Active'}</span></td>
                  <td style={{ ...styles.td, ...styles.subtle }}>{formatDate(form.lastModified || form.createdAt)}</td>
                  <td style={styles.td}><button type="button" style={styles.action} onClick={() => window.open(adminService.getFormPreview(form.id), '_blank', 'noopener,noreferrer')}>View template</button></td>
                </tr>)}</tbody>
              </table></div>
            )}

            {activeTab === 'links' && (
              visibleLinks.length === 0 ? <div style={styles.empty}>No links found for this company.</div> :
              <div style={styles.tableWrap}><table style={styles.table}>
                <thead><tr><th style={styles.th}>Template</th><th style={styles.th}>Recipient</th><th style={styles.th}>Status</th><th style={styles.th}>Created</th><th style={styles.th}>Expires</th><th style={styles.th}>Action</th></tr></thead>
                <tbody>{visibleLinks.map((link) => <tr key={link.token}>
                  <td style={styles.td}>{link.formName || 'Unknown'}</td>
                  <td style={styles.td}>{link.patientName || link.patientEmail || link.doctorEmail || '—'}</td>
                  <td style={styles.td}><span style={link.isExpired ? styles.badge('#fee2e2', '#991b1b') : styles.badge('#dcfce7', '#166534')}>{link.isExpired ? 'Expired' : link.isUsed ? 'Used' : 'Active'}</span></td>
                  <td style={{ ...styles.td, ...styles.subtle }}>{formatDate(link.createdAt)}</td>
                  <td style={{ ...styles.td, ...styles.subtle }}>{formatDate(link.expiresAt)}</td>
                  <td style={styles.td}>
                    <button type="button" style={styles.action} onClick={() => copyLink(link)}>Copy</button>
                    <button type="button" style={styles.action} onClick={() => window.open(link.url || `/fill/${link.token}`, '_blank', 'noopener,noreferrer')}>Open</button>
                  </td>
                </tr>)}</tbody>
              </table></div>
            )}

            {activeTab === 'submissions' && (
              visibleSubmissions.length === 0 ? <div style={styles.empty}>No submissions found for this company.</div> :
              <div style={styles.tableWrap}><table style={styles.table}>
                <thead><tr><th style={styles.th}>Reference</th><th style={styles.th}>Template</th><th style={styles.th}>Patient / file</th><th style={styles.th}>Status</th><th style={styles.th}>Submitted</th><th style={styles.th}>Action</th></tr></thead>
                <tbody>{visibleSubmissions.map((item, index) => <tr key={`${item.formId}-${item.filename}-${index}`}>
                  <td style={{ ...styles.td, ...styles.subtle }}>{item.referenceId || '—'}</td>
                  <td style={styles.td}>{item.formName || 'Unknown'}</td>
                  <td style={styles.td}>{item.patientName || item.filename || '—'}</td>
                  <td style={styles.td}><span style={styles.badge('#eef2ff', '#4338ca')}>{item.status || item.submissionSource || 'Submitted'}</span></td>
                  <td style={{ ...styles.td, ...styles.subtle }}>{formatDate(item.uploadedAt || item.createdAt)}</td>
                  <td style={styles.td}><button type="button" style={styles.action} onClick={() => previewSubmission(item)}>View PDF</button></td>
                </tr>)}</tbody>
              </table></div>
            )}
          </>
        )}
      </div>

      {preview && (
        <div style={styles.overlay} onClick={() => setPreview(null)}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHead}>
              <strong>{preview.name}</strong>
              <button type="button" style={styles.close} onClick={() => setPreview(null)}>×</button>
            </div>
            <iframe title={preview.name} src={preview.url} style={{ width: '100%', flex: 1, border: 'none' }} />
          </div>
        </div>
      )}
    </div>
  );
}
